import type { App, BlockDef } from "@cogo/types-v2";
import { sha256ActionConsumerText } from "./action-consumer-contract.js";
import { createAppRuntimeCore } from "./app-runtime-core.js";

const TICK_PROOF_SCHEMA_VERSION = "cogo.tick-flow-execution-proof.v1" as const;
const TICK_PROOF_RUNTIME =
  "@cogo/runtime-v2/createAppRuntimeCore.runTickFlows" as const;

export interface TickFlowExecutionReceipt {
  schemaVersion: typeof TICK_PROOF_SCHEMA_VERSION;
  status: "pass" | "blocked";
  runtime: typeof TICK_PROOF_RUNTIME;
  runId: string;
  observedAt: string;
  sourceRefs: [
    "packages/cogo-react-renderer-v2/src/components/App.tsx",
    "packages/cogo-runtime-v2/src/app-runtime-core.ts",
  ];
  evidence: {
    requestedDt: number;
    observedDt: number | null;
    immediateTickRequests: 2;
    activeTickExecutions: number;
    inactiveTickExecutions: number;
    noOverlapObserved: boolean;
  };
  contentHash: `sha256:${string}`;
}

export async function buildTickFlowExecutionReceipt(input: {
  runId: string;
  observedAt: string;
  dt: number;
}): Promise<TickFlowExecutionReceipt> {
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,159}$/u.test(input.runId)) {
    throw new Error("COGO_TICK_FLOW_PROOF_RUN_ID_INVALID");
  }
  if (!Number.isFinite(input.dt) || input.dt < 0) {
    throw new Error("COGO_TICK_FLOW_PROOF_DT_INVALID");
  }
  if (Number.isNaN(Date.parse(input.observedAt))) {
    throw new Error("COGO_TICK_FLOW_PROOF_OBSERVED_AT_INVALID");
  }

  const active = proofBlock(
    "active",
    "activeTickExecutions",
    "activeObservedDt",
    true,
  );
  const inactive = proofBlock(
    "inactive",
    "inactiveTickExecutions",
    "inactiveObservedDt",
    false,
  );
  const manifest = proofManifest();
  const runtime = createAppRuntimeCore(manifest, { active, inactive });

  try {
    runtime.registerAllFlows();
    await runtime.runInitFlows();
    runtime.runTickFlows(input.dt);
    runtime.runTickFlows(input.dt + 1);
    await settleTickBatch();

    const activeTickExecutions = Number(
      runtime.stateManager.get("activeTickExecutions") ?? 0,
    );
    const inactiveTickExecutions = Number(
      runtime.stateManager.get("inactiveTickExecutions") ?? 0,
    );
    const rawObservedDt = runtime.stateManager.get("activeObservedDt");
    const observedDt = typeof rawObservedDt === "number" ? rawObservedDt : null;
    const noOverlapObserved = activeTickExecutions === 1;
    const status: TickFlowExecutionReceipt["status"] =
      observedDt === input.dt &&
      activeTickExecutions === 1 &&
      inactiveTickExecutions === 0 &&
      noOverlapObserved
        ? "pass"
        : "blocked";
    const body = {
      schemaVersion: TICK_PROOF_SCHEMA_VERSION,
      status,
      runtime: TICK_PROOF_RUNTIME,
      runId: input.runId,
      observedAt: input.observedAt,
      sourceRefs: [
        "packages/cogo-react-renderer-v2/src/components/App.tsx",
        "packages/cogo-runtime-v2/src/app-runtime-core.ts",
      ] as TickFlowExecutionReceipt["sourceRefs"],
      evidence: {
        requestedDt: input.dt,
        observedDt,
        immediateTickRequests: 2 as const,
        activeTickExecutions,
        inactiveTickExecutions,
        noOverlapObserved,
      },
    };
    return {
      ...body,
      contentHash: contentHash(body),
    };
  } finally {
    runtime.destroy();
  }
}

export function verifyTickFlowExecutionReceipt(
  value: unknown,
): asserts value is TickFlowExecutionReceipt {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("COGO_TICK_FLOW_PROOF_INVALID");
  }
  const receipt = value as Record<string, unknown>;
  const declared = receipt.contentHash;
  if (
    typeof declared !== "string" ||
    !/^sha256:[0-9a-f]{64}$/u.test(declared)
  ) {
    throw new Error("COGO_TICK_FLOW_PROOF_CONTENT_HASH_INVALID");
  }
  const { contentHash: _contentHash, ...body } = receipt;
  if (contentHash(body) !== declared) {
    throw new Error("COGO_TICK_FLOW_PROOF_CONTENT_HASH_INVALID");
  }
  const evidence = receipt.evidence as Record<string, unknown> | undefined;
  const sourceRefs = receipt.sourceRefs;
  if (
    !hasExactKeys(receipt, [
      "contentHash",
      "evidence",
      "observedAt",
      "runId",
      "runtime",
      "schemaVersion",
      "sourceRefs",
      "status",
    ]) ||
    typeof receipt.runId !== "string" ||
    !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,159}$/u.test(receipt.runId) ||
    typeof receipt.observedAt !== "string" ||
    Number.isNaN(Date.parse(receipt.observedAt)) ||
    !Array.isArray(sourceRefs) ||
    sourceRefs.length !== 2 ||
    sourceRefs[0] !==
      "packages/cogo-react-renderer-v2/src/components/App.tsx" ||
    sourceRefs[1] !== "packages/cogo-runtime-v2/src/app-runtime-core.ts" ||
    !evidence ||
    !hasExactKeys(evidence, [
      "activeTickExecutions",
      "immediateTickRequests",
      "inactiveTickExecutions",
      "noOverlapObserved",
      "observedDt",
      "requestedDt",
    ]) ||
    receipt.schemaVersion !== TICK_PROOF_SCHEMA_VERSION ||
    receipt.runtime !== TICK_PROOF_RUNTIME ||
    receipt.status !== "pass" ||
    evidence?.requestedDt !== evidence?.observedDt ||
    evidence?.immediateTickRequests !== 2 ||
    evidence?.activeTickExecutions !== 1 ||
    evidence?.inactiveTickExecutions !== 0 ||
    evidence?.noOverlapObserved !== true
  ) {
    throw new Error("COGO_TICK_FLOW_PROOF_EVIDENCE_INVALID");
  }
}

function hasExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
): boolean {
  const actual = Object.keys(value).sort();
  const wanted = [...expected].sort();
  return (
    actual.length === wanted.length &&
    actual.every((key, index) => key === wanted[index])
  );
}

function proofManifest(): App {
  return {
    name: "tick-flow-proof",
    version: "1.0.0",
    pages: [
      {
        id: "home",
        route: "/",
        layout: "stack",
        blocks: ["active"],
        blockLayout: {},
      },
    ],
    globalState: {},
    datasources: {},
    navigation: { defaultRoute: "/" },
  } as unknown as App;
}

function proofBlock(
  name: string,
  executionTarget: string,
  dtTarget: string,
  delayed: boolean,
): BlockDef {
  return {
    meta: {
      name,
      type: "ui",
      version: "1.0.0",
      description: `Tick proof block ${name}`,
      stateContract: { localState: {}, inputSymbols: [], outputSymbols: [] },
    },
    ui: { root: { id: `${name}-root`, type: "container", children: [] } },
    events: {},
    flow: {
      flows: {
        tick: {
          trigger: "tick",
          steps: [
            ...(delayed
              ? [{ type: "basic", action: "delay", params: { ms: 25 } }]
              : []),
            {
              type: "basic",
              action: "setState",
              params: {
                target: executionTarget,
                value: `#{local.${executionTarget} + 1}`,
              },
            },
            {
              type: "basic",
              action: "setState",
              params: { target: dtTarget, value: "#{event.dt}" },
            },
          ],
        },
      },
    },
    state: {
      state: {
        [executionTarget]: { type: "number", default: 0 },
        [dtTarget]: { type: "number", default: -1 },
      },
    },
    data: {},
  } as unknown as BlockDef;
}

async function settleTickBatch(): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, 80));
}

function contentHash(value: unknown): `sha256:${string}` {
  return `sha256:${sha256ActionConsumerText(stableJson(value))}`;
}

function stableJson(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`)
    .join(",")}}`;
}
